import { useEffect, useState } from "react";
import { X, Award, Lock, Sparkles, Crown, Skull, Flame, Swords } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Achievements — permanent one-time trophies that complement Daily Pacts.
 * Grouped by tier (Bronze → Silver → Gold → Mythic) with lifetime-progress bars
 * and a claimed-on timestamp on unlocked rows. Souls reward drops the moment
 * the target is hit — done server-side, race-safe.
 */
const TIERS = {
  bronze: { label: "BRONZE", color: "#cd7f32", glow: "rgba(205,127,50,0.35)" },
  silver: { label: "SILVER", color: "#c0c0c0", glow: "rgba(192,192,192,0.35)" },
  gold:   { label: "GOLD",   color: "#d4af37", glow: "rgba(212,175,55,0.5)"  },
  mythic: { label: "MYTHIC", color: "#e25822", glow: "rgba(226,88,34,0.55)"  },
};

const KIND_ICON = {
  kills_total:   Skull,
  first_boss:    Crown,
  bosses_total:  Crown,
  reach_f5:      Flame,
  reach_f10:     Flame,
  first_victory: Award,
  crits_total:   Sparkles,
};

export default function AchievementsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/achievements");
        if (alive) setData(r);
      } catch {
        if (alive) setData({ achievements: [], unlocked: 0, total: 0, completion: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;
  const rows = data?.achievements || [];

  // Group by tier for section headers
  const grouped = rows.reduce((acc, a) => {
    (acc[a.tier] = acc[a.tier] || []).push(a);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "rgba(4,2,1,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="achievements-modal-backdrop"
    >
      <div
        className="panel w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: "radial-gradient(ellipse at top, rgba(90,60,10,0.3), rgba(8,4,2,0.98))",
          borderColor: "rgba(212,175,55,0.55)",
          boxShadow: "0 0 40px rgba(212,175,55,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="achievements-modal"
      >
        <div className="flex items-center justify-between p-5 pb-3 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text">— TROPHIES OF THE DEEP —</div>
            <div className="font-heading text-3xl italic text-[#f0e6d2] mt-1">Ember Ledger</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="font-heading text-3xl gold-text" data-testid="achievements-completion">
                {data?.completion ?? 0}%
              </div>
              <div className="text-[10px] tracking-widest ash-text">
                {data?.unlocked ?? 0} / {data?.total ?? 0} CLAIMED
              </div>
            </div>
            <button
              onClick={onClose}
              className="panel p-2 hover:border-[#d4af37] transition-colors"
              data-testid="achievements-close-btn"
            >
              <X size={16} className="ash-text" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="text-center ash-text italic py-16">The scribe unrolls the ledger...</div>
          )}
          {!loading && ["bronze", "silver", "gold", "mythic"].map((tier) => {
            const list = grouped[tier];
            if (!list?.length) return null;
            const meta = TIERS[tier];
            return (
              <div key={tier}>
                <div
                  className="text-[10px] tracking-[0.45em] mb-2 flex items-center gap-2"
                  style={{ color: meta.color }}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: meta.color, boxShadow: `0 0 8px ${meta.glow}` }}
                  />
                  {meta.label}
                </div>
                <div className="space-y-2">
                  {list.map((a) => <AchievementRow key={a.id} a={a} tierMeta={meta} />)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-white/10 text-center text-[10px] tracking-[0.3em] ash-text">
          Every trophy pours souls into your ember. Once claimed — claimed forever.
        </div>
      </div>
    </div>
  );
}

function AchievementRow({ a, tierMeta }) {
  const Icon = KIND_ICON[a.kind] || Award;
  const unlocked = !!a.unlocked;
  const pct = Math.min(100, (a.progress / a.target) * 100);
  const claimedDate = a.unlocked_at ? new Date(a.unlocked_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

  return (
    <div
      className="panel p-3 flex items-center gap-3"
      style={{
        borderColor: unlocked ? tierMeta.color : "rgba(255,255,255,0.08)",
        background: unlocked ? `linear-gradient(90deg, ${tierMeta.glow.replace(/,\s*[\d.]+\)/, ",0.10)")}, transparent)` : "transparent",
        boxShadow: unlocked ? `0 0 14px ${tierMeta.glow} inset` : "none",
        opacity: unlocked ? 1 : 0.85,
      }}
      data-testid={`achievement-${a.id}`}
    >
      <div
        className="w-11 h-11 flex items-center justify-center flex-shrink-0 border"
        style={{
          borderColor: unlocked ? tierMeta.color : "rgba(255,255,255,0.15)",
          background: "rgba(0,0,0,0.4)",
          boxShadow: unlocked ? `0 0 12px ${tierMeta.glow}` : "none",
        }}
      >
        {unlocked ? (
          <Icon size={18} style={{ color: tierMeta.color, filter: `drop-shadow(0 0 4px ${tierMeta.glow})` }} />
        ) : (
          <Lock size={14} className="ash-text" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 justify-between">
          <div
            className={`font-heading text-lg italic truncate ${unlocked ? "" : "text-[#f0e6d2]"}`}
            style={unlocked ? { color: tierMeta.color, textShadow: `0 0 8px ${tierMeta.glow}` } : {}}
          >
            {a.title}
          </div>
          <div className={`text-[10px] tabular-nums tracking-widest flex-shrink-0 ${unlocked ? "gold-text" : "ash-text"}`}>
            {unlocked
              ? <span className="flex items-center gap-1"><Sparkles size={10} /> CLAIMED{claimedDate && ` · ${claimedDate}`}</span>
              : <>{a.progress} / {a.target}</>}
          </div>
        </div>
        <div className="text-[11px] font-narrative italic ash-text mt-0.5 truncate">{a.desc}</div>
        {!unlocked && (
          <div className="h-1 mt-1.5 bg-black/60 border border-white/10 overflow-hidden">
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${tierMeta.color}, ${tierMeta.color}80)`,
                boxShadow: `0 0 8px ${tierMeta.glow} inset`,
              }}
            />
          </div>
        )}
      </div>
      <div
        className={`text-right flex-shrink-0 ${unlocked ? "" : "ash-text"}`}
        style={unlocked ? { color: tierMeta.color } : {}}
      >
        <div className="font-heading text-xl flex items-center gap-1">
          <Swords size={11} /> {a.reward}
        </div>
        <div className="text-[9px] tracking-widest">SOULS</div>
      </div>
    </div>
  );
}
